import Review from '@/models/review';
import stringFormatter from '@/utils/format/string';
import { ActionContext, ActionTree, GetterTree, MutationTree } from 'vuex';
import { RootState } from '@/store';
import orderApi from '@/api/order';
import TakeAway from '@/models/takeAway';
import OrderStaff from '@/models/orderStaff';
import { generateActualOrdersForDayStructure } from '@/utils';
import User from '@/models/user';
import { TakeAwayProps } from '@/interfaces/models/order';

const namespaced: boolean = true;

export interface TakeAwayState {
  sectionNotification: number;
  isActualOrdersFetching: boolean,
  isActualOrdersWasFetch: boolean,
  actualOrders: TakeAway[]
}

const getDefaultState = (): TakeAwayState => {
  return {
    sectionNotification: 0,
    isActualOrdersFetching: false,
    isActualOrdersWasFetch: false,
    actualOrders: []
  };
};

const state: TakeAwayState = getDefaultState();

const getters: GetterTree<TakeAwayState, RootState> = {
  todayOrders(state, getters, rootState: any) {
    const todayHash = rootState.timer.today.getFullHash();

    return getters.actualOrdersGroupedByDay[todayHash] || [];
  },
  actualOrdersGroupedByDay(state, getters, rootState: any) {
    const reducer = (groupedOrders: any, order: TakeAway) => {
      const orderDateHash = order.date.getFullHash();

      if (orderDateHash === rootState.timer.today.getFullHash()) {
        const hours = order.date.getHours();
        const minutes = Math.floor(order.date.getMinutes() / 5);

        if (!groupedOrders[orderDateHash]) {
          groupedOrders[orderDateHash] = generateActualOrdersForDayStructure();
        }

        groupedOrders[orderDateHash][hours][minutes].push(order);
      }

      return groupedOrders;
    };

    return state.actualOrders.reduce(reducer, {
      [rootState.timer.today.getFullHash()]: generateActualOrdersForDayStructure()
    });
  },
  oldActualOrdersGroupedByDay(state, getters, rootState: any) {
    const todayTime = rootState.timer.today.getLocalTime();
    const reducer = (groupedOrders: any, order: TakeAway) => {
      const orderTimestamp = order.date.getStartOfDay().getLocalTime();

      if (orderTimestamp < todayTime && !order.isDone) {
        const hours = order.date.getHours();
        const minutes = Math.floor(order.date.getMinutes() / 5);

        if (!groupedOrders[orderTimestamp]) {
          groupedOrders[orderTimestamp] = {};
        }

        if (!groupedOrders[orderTimestamp][hours]) {
          groupedOrders[orderTimestamp][hours] = {};
        }

        if (!groupedOrders[orderTimestamp][hours][minutes]) {
          groupedOrders[orderTimestamp][hours][minutes] = [];
        }


        groupedOrders[orderTimestamp][hours][minutes].push(order);
      }

      return groupedOrders;
    };

    return state.actualOrders.reduce(reducer, {});
  },
  todayActualOrders(state, getters, rootState: any) {
    return state.actualOrders.filter((order: TakeAway) => {
      return order.date.getStartOfDay().isSameDays(rootState.timer.today);
    });
  },
  cookingOrdersByStaff(state, getters, rootState: any) {
    if (rootState.user.user !== null) {
      return state.actualOrders.filter((takeAway: TakeAway) => {
        return takeAway.isCooking && takeAway.staff && takeAway.staff.id === rootState.user.user.id;
      });
    }
  },
  completedOrdersCountByStaff(state, getters, rootState: any) {
    return getters.completedOrders
      .filter(({ staff }: TakeAway) => staff && staff.id === rootState.user.user.id)
      .length;
  },
  completedOrders(state) {
    return state.actualOrders.filter(({ isDone }: TakeAway) => isDone);
  },
  isHaveOrders(state, getters) {
    return getters.todayActualOrders.length;
  },
  isHaveUndoneTodayOrders(state, getters) {
    return getters.todayActualOrders.filter(({ isDone }: TakeAway) => !isDone).length > 0;
  }
};

const actions: ActionTree<TakeAwayState, RootState> = {
  async fetchTodayAndUndoneOrders({ commit, state }: ActionContext<TakeAwayState, RootState>) {
    if (!state.isActualOrdersFetching && !state.isActualOrdersWasFetch) {
      commit('setIsActualOrdersFetching', true);

      return orderApi.fetchActualOrders()
        .then((actualOrders: any) => {
          commit('setActualOrders', actualOrders);
          commit('setIsActualOrdersFetching', false);
          commit('setIsActualOrdersWasFetch', true);
        });
    }
  },
  async takeOrder({ dispatch, rootState }: ActionContext<TakeAwayState, any>, order: TakeAway) {
    await orderApi.takeOrder(order.id);

    const user: User = rootState.user.user;

    return dispatch('socketTakeTakeAwayOrder', {
      ...order,
      isCooking: true,
      staff: {
        id: user.id,
        firstName: user.firstName,
        lastName: user.lastName,
        avatar: user.avatar.path
      }
    }, { root: true });
  },
  async refuseOrder({ dispatch }: ActionContext<TakeAwayState, RootState>, orderId: string) {
    await orderApi.refuseOrder(orderId);

    return dispatch('socketRefuseTakeAwayOrder', { id: orderId }, { root: true });
  },
  async doneOrder({ dispatch }: ActionContext<TakeAwayState, RootState>, order: TakeAway) {
    await orderApi.doneOrder(order.id);

    dispatch('socketWriteOffProducts', order.getWriteOffPayload(), { root: true });

    return dispatch('socketDoneTakeAwayOrder', order, { root: true });
  },
  async undoneOrder({ dispatch }: ActionContext<TakeAwayState, RootState>, order: TakeAway) {
    await orderApi.undoneOrder(order.id);

    return dispatch('socketUndoneTakeAwayOrder', order, { root: true });
  },
  async doneOrderProduct({ dispatch }: ActionContext<TakeAwayState, RootState>, { orderId, productId }: any) {
    await orderApi.doneOrderProduct(productId);

    return dispatch('socketDoneOrderProduct', { orderId, productId }, { root: true });
  },
  async undoneOrderProduct({ dispatch }: ActionContext<TakeAwayState, RootState>, { orderId, productId }: any) {
    await orderApi.undoneOrderProduct(productId);

    return dispatch('socketUndoneOrderProduct', { orderId, productId }, { root: true });
  },
  async removeTakeAway({ dispatch }: ActionContext<TakeAwayState, RootState>, orderId: string) {
    await orderApi.removeOrder(orderId);

    return dispatch('socketRemoveTakeAwayOrder', { id: orderId }, { root: true });
  },
  async filterActualOrders({ commit, state, rootState }: any) {
    const actualOrders = state.actualOrders.filter((order: TakeAway) => {
      return order.isActualOrderByToday(rootState.timer.today);
    });

    commit('setActualOrders', actualOrders);
  }
};

const mutations: MutationTree<TakeAwayState> = {
  resetState(state) {
    Object.assign(state, getDefaultState());
  },
  setSectionNotification(state, sectionNotification: any) {
    state.sectionNotification = sectionNotification;
  },
  setActualOrders(state, actualOrders: TakeAway[]) {
    state.actualOrders = actualOrders;
  },
  createTakeAway(state, takeAwayProps: TakeAwayProps) {
    const takeAway = TakeAway.createFromRequest(takeAwayProps);

    state.actualOrders.push(takeAway);
  },
  removeTakeAway(state, orderId: string) {
    const takeAwayIndex = state.actualOrders.findIndex(({ id }: TakeAway) => id === orderId);

    if (takeAwayIndex !== -1) {
      state.actualOrders.splice(takeAwayIndex, 1);
    }
  },
  takeOrder(state, { id: orderId, staff }: any) {
    const order: TakeAway | undefined = state.actualOrders.find(({ id }) => id === orderId);

    if (order !== undefined) {
      order.take(new OrderStaff(staff));
    }
  },
  refuseOrder(state, orderId: string) {
    const order: TakeAway | undefined = state.actualOrders.find(({ id }) => id === orderId);

    if (order !== undefined) {
      order.refuse();
    }
  },
  doneOrder(state, orderId: string) {
    const order: TakeAway | undefined = state.actualOrders.find(({ id }) => id === orderId);

    if (order !== undefined) {
      order.done();
    }
  },
  undoneOrder(state, orderId: string) {
    const order: TakeAway | undefined = state.actualOrders.find(({ id }) => id === orderId);

    if (order !== undefined) {
      order.undone();
    }
  },
  doneOrderProduct(state, { orderId, productId }: any) {
    const order: TakeAway | undefined = state.actualOrders.find(({ id }) => id === orderId);

    if (order !== undefined) {
      order.doneProduct(productId);
    }
  },
  undoneOrderProduct(state, { orderId, productId }: any) {
    const order: TakeAway | undefined = state.actualOrders.find(({ id }) => id === orderId);

    if (order !== undefined) {
      order.undoneProduct(productId);
    }
  },
  createReview(state, payload: any) {
    const order = state.actualOrders.find(({ id }: TakeAway) => id === payload.id);

    if (undefined !== order) {
      order.review = new Review(payload.review);
    }
  },
  setIsActualOrdersFetching(state, isActualOrdersFetching) {
    state.isActualOrdersFetching = isActualOrdersFetching;
  },
  setIsActualOrdersWasFetch(state, isActualOrdersWasFetch) {
    state.isActualOrdersWasFetch = isActualOrdersWasFetch;
  }
};

export default {
  namespaced,
  state,
  getters,
  actions,
  mutations
};
